import { memo, useEffect, useRef, useState } from 'react'
import type { NodeProps } from '@xyflow/react'
import { NodeResizer, NodeToolbar, Position } from '@xyflow/react'
import { MIRO_FONTS, loadAllMiroFonts, loadMiroFont, miroFont, type MiroFontKey, type TextStyle } from '@shared/data/miroFonts'

/**
 * Text on the board (Huanrui, 2026-10-12)
 * -------------------------------------------------------------
 * Words set in type, unlike the sticky note's handwriting on paper: no
 * background, a typeface, a size, bold or not, aligned left, centre or right,
 * any colour. The typefaces are Miro's own (shared/data/miroFonts.ts), the
 * same list the Mood board page offers, so a text set here looks the same
 * when it is taken to a board.
 *
 * Like the note: at rest it moves when dragged, a double-click (or arriving
 * new) opens it for writing, clicking away or Escape closes it, and one go of
 * writing is one step on the undo stack. Chosen, its type controls float
 * above it. The width is the box's; the height follows the words.
 *
 * Only the words and their style are kept with the board; a typeface comes
 * from Google Fonts the first time a text on the page uses it.
 */

/** The two colours the board itself gives new text, one per kind of paper. */
const BOARD_INKS = new Set(['#f2f2f2', '#111111'])

export const DEFAULT_LABEL_STYLE: TextStyle = { font: 'open_sans', size: 28, bold: false, align: 'left', color: '#f2f2f2' }

export interface LabelNodeData extends Record<string, unknown> {
  text?: string
  textStyle?: TextStyle
  /** Handed in by the board, never stored. */
  onText?: (text: string) => void
  onStyle?: (style: TextStyle) => void
  onWriteStart?: () => void
}

export const LabelNode = memo(function LabelNode({ data, selected }: NodeProps) {
  const label = data as LabelNodeData
  const style = { ...DEFAULT_LABEL_STYLE, ...(label.textStyle ?? {}) }
  const [writing, setWriting] = useState(() => !label.text)
  const box = useRef<HTMLTextAreaElement | null>(null)

  useEffect(() => loadMiroFont(style.font), [style.font])

  // As tall as its words.
  useEffect(() => {
    const area = box.current
    if (!writing || !area) return
    area.style.height = 'auto'
    area.style.height = `${area.scrollHeight}px`
  })

  useEffect(() => {
    if (!writing) return
    label.onWriteStart?.()
    const area = box.current
    if (!area) return
    area.focus()
    area.setSelectionRange(area.value.length, area.value.length)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [writing])

  const css = {
    fontFamily: miroFont(style.font).css,
    fontSize: `${style.size}px`,
    fontWeight: style.bold ? 700 : 400,
    textAlign: style.align,
    // Text left in the board's own ink -- the white a dark paper gives a new
    // one, or the near-black a light paper gives -- follows the paper when it
    // changes (2026-10-21), so it never ends up white on white. A colour
    // somebody chose stays what they chose.
    color: BOARD_INKS.has(style.color.toLowerCase()) ? `var(--board-label, ${style.color})` : style.color,
  } as const
  const set = <K extends keyof TextStyle>(key: K, value: TextStyle[K]) => label.onStyle?.({ ...style, [key]: value })

  return (
    <div className={`clabel${selected ? ' clabel--selected' : ''}${writing ? ' clabel--writing' : ''}`}>
      <NodeResizer
        isVisible={selected}
        minWidth={60}
        minHeight={24}
        lineClassName="cnode__resize-line"
        handleClassName="cnode__resize-handle"
      />
      <NodeToolbar className="clabel__toolbar" isVisible={selected} position={Position.Top} align="start" offset={10}>
        <div className="clabel__bar nodrag nopan" onPointerDown={(event) => event.stopPropagation()}>
          <select
            className="clabel__font"
            // Each name in its own face: every typeface is fetched when the list is first reached for.
            onPointerEnter={loadAllMiroFonts}
            onFocus={loadAllMiroFonts}
            value={style.font}
            onChange={(event) => set('font', event.target.value as MiroFontKey)}
            aria-label="Typeface"
            style={{ fontFamily: miroFont(style.font).css }}
          >
            {MIRO_FONTS.map((font) => (
              <option key={font.key} value={font.key} style={{ fontFamily: font.css }}>
                {font.label}
              </option>
            ))}
          </select>
          <input
            className="clabel__size"
            type="number"
            min={8}
            max={400}
            value={style.size}
            onChange={(event) => set('size', Math.max(8, Math.min(400, Number(event.target.value) || style.size)))}
            onKeyDown={(event) => event.stopPropagation()}
            aria-label="Size"
          />
          <button type="button" className={`clabel__tog${style.bold ? ' on' : ''}`} onClick={() => set('bold', !style.bold)} aria-label="Bold">
            <b>B</b>
          </button>
          {(['left', 'center', 'right'] as const).map((align) => (
            <button
              type="button"
              key={align}
              className={`clabel__tog${style.align === align ? ' on' : ''}`}
              onClick={() => set('align', align)}
              aria-label={`Align ${align}`}
            >
              <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true">
                {[12, 8, 10].map((w, i) => (
                  <rect
                    key={i}
                    x={align === 'left' ? 2 : align === 'right' ? 14 - w : (16 - w) / 2}
                    y={3 + i * 4}
                    width={w}
                    height="1.6"
                    rx="0.8"
                    fill="currentColor"
                  />
                ))}
              </svg>
            </button>
          ))}
          <input className="clabel__colour" type="color" value={style.color} onChange={(event) => set('color', event.target.value)} aria-label="Colour" />
        </div>
      </NodeToolbar>
      {writing ? (
        <textarea
          ref={box}
          className="clabel__text clabel__area nodrag nowheel nopan"
          rows={1}
          value={label.text ?? ''}
          placeholder="Text"
          spellCheck
          style={css}
          onChange={(event) => label.onText?.(event.target.value)}
          onBlur={() => setWriting(false)}
          onKeyDown={(event) => {
            event.stopPropagation()
            if (event.key === 'Escape') event.currentTarget.blur()
          }}
        />
      ) : (
        <div
          className={`clabel__text${label.text ? '' : ' clabel__text--empty'}`}
          style={css}
          onDoubleClick={(event) => {
            event.stopPropagation()
            setWriting(true)
          }}
        >
          {label.text || 'Double-click to write'}
        </div>
      )}
    </div>
  )
})
